import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./Public.css";
import Footer from "../../components/Footer";
import useSeo from "../../hooks/useSeo";
import { FAQS } from "../../data/faqs";

const ASSET = process.env.PUBLIC_URL || "";

const pubUrl= process.env.PUBLIC_URL;

// --- helpers ---
function useIsDesktop(minWidth = 1024) {
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== "undefined" ? window.innerWidth >= minWidth : true
  );

  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= minWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [minWidth]);

  return isDesktop;
}

function useInView(ref, { threshold = 0.2, rootMargin = "0px 0px -10% 0px" } = {}) {
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          io.disconnect(); // animate once
        }
      },
      { threshold, rootMargin }
    );

    io.observe(el);
    return () => io.disconnect();
  }, [ref, threshold, rootMargin]);

  return inView;
}

export default function Landing() {
  useSeo({
  title: "Crowned Roofing | Commercial Roofing Contractor in Toronto, ON",
  description:
    "Commercial roofing inspections, repairs, and replacement planning in Toronto. Clear documentation, practical scopes, and professional execution.",
  ogImage: "https://www.crownedroofing.com/images/ogimg.png",
});


  const navigate = useNavigate();
  const isDesktop = useIsDesktop(1024);

  // refs for sections
  const sOverviewRef = useRef(null);
  const sCasesRef = useRef(null);
  const sFeedbackRef = useRef(null);
  const sChoiceRef = useRef(null);

  const overviewIn = useInView(sOverviewRef, { threshold: 0.25 });
  const casesIn = useInView(sCasesRef, { threshold: 0.2 });
  const feedbackIn = useInView(sFeedbackRef, { threshold: 0.2 });
  const choiceIn = useInView(sChoiceRef, { threshold: 0.2 });
  const featuredFaqs = FAQS.slice(0, 3);

  // decision background hover (crossfade)
  const [choiceBg, setChoiceBg] = useState("none"); // "none" | "res" | "com"
  const [roofingOpen, setRoofingOpen] = useState(false);
  const [constructionOpen, setConstructionOpen] = useState(false);

  const bgImages = useMemo(
    () => ({
      res: `${ASSET}/images/landing/choice-residential.jpg`,
      com: `${ASSET}/images/landing/choice-commercial.jpg`,
    }),
    [ASSET]
  );

  // preload hover backgrounds
  useEffect(() => {
    const imgs = [bgImages.res, bgImages.com].map((src) => {
      const img = new Image();
      img.src = src;
      return img;
    });
    return () => {
      // no-op
    };
  }, [bgImages]);

  const goResidential = () => navigate("/residential");
  const goCommercial = () => navigate("/commercial");

  return (
    <main className={`landing ${isDesktop ? "snap" : ""}`}>
      {/* HERO */}
      <section className="lp hero snap-section">
        <div className="hero-bg" aria-hidden="true" style={{backgroundImage: `url(${pubUrl}/images/landing/landing-hero.png)`}}/>
        <div className="hero-inner">
          <div className="brand">
            <img src={pubUrl+'/images/landing/logo.png'} alt='logo'/>
          </div>

          <nav className="nav">
            <div className="navDropdown" onMouseLeave={() => setRoofingOpen(false)}>
              <button
                type="button"
                className="navLink navDropdownTrigger"
                aria-haspopup="true"
                aria-expanded={roofingOpen}
                onClick={() => setRoofingOpen((open) => !open)}
                onMouseEnter={() => setRoofingOpen(true)}
              >
                ROOFING
              </button>
              <div className={roofingOpen ? "navDropdownMenu open" : "navDropdownMenu"}>
                <button onClick={goCommercial} className="navDropdownLink">COMMERCIAL</button>
                <button onClick={goResidential} className="navDropdownLink">RESIDENTIAL</button>
                <button onClick={() => navigate("/inspections")} className="navDropdownLink">INSPECTIONS</button>
              </div>
            </div>
            <div className="navDropdown" onMouseLeave={() => setConstructionOpen(false)}>
              <button
                type="button"
                className="navLink navDropdownTrigger"
                aria-haspopup="true"
                aria-expanded={constructionOpen}
                onClick={() => setConstructionOpen((open) => !open)}
                onMouseEnter={() => setConstructionOpen(true)}
              >
                CONSTRUCTION
              </button>
              <div className={constructionOpen ? "navDropdownMenu open" : "navDropdownMenu"}>
                <button onClick={() => navigate("/fencing")} className="navDropdownLink">FENCING</button>
                <button onClick={() => navigate("/deck-patio")} className="navDropdownLink">DECK &amp; PATIO</button>
                <button onClick={() => navigate("/siding")} className="navDropdownLink">SIDING</button>
                <button onClick={() => navigate("/interior-renovations")} className="navDropdownLink">INTERIOR RENOVATIONS</button>
              </div>
            </div>
            <button onClick={() => navigate("/portfolio")} className="navLink">PORTFOLIO</button>
            <button onClick={() => navigate("/contact")} className="navLink navCta">CONTACT US</button>
          </nav>

          {/* Marquee */}
          <div className="marqueeWrap" aria-label="Residential and Commercial Roofing Services">
            <div className="marquee">
              <span className="marqueeText">
                <button className="marqueeLink" onClick={goResidential}>RESIDENTIAL</button>
                {" "} &amp; {" "}
                <button className="marqueeLink" onClick={goCommercial}>COMMERCIAL</button>
                {" "} ROOFING SERVICES • {" "}
                <button className="marqueeLink" onClick={goResidential}>RESIDENTIAL</button>
                {" "} &amp; {" "}
                <button className="marqueeLink" onClick={goCommercial}>COMMERCIAL</button>
                {" "} ROOFING SERVICES
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* OVERVIEW */}
      <section className="lp overview snap-section" ref={sOverviewRef}>
        <div className={`overviewStage ${overviewIn ? "in" : ""}`}>
          {/* Edge buildings */}
          <img
            className="edgeBuilding left"
            src={`${pubUrl}/images/landing/house.png`}
            alt=""
            aria-hidden="true"
          />
          <img
            className="edgeBuilding right"
            src={`${pubUrl}/images/landing/commercial.png`}
            alt=""
            aria-hidden="true"
          />

          <div className="overviewContent">
            <h2 className="sectionTitle">
              Roofing Services, Clearly Defined
            </h2>

            <p>
              Crowned Roofing provides residential and commercial roofing services for property owners who want clear
              information and dependable execution. Our work includes roof repairs, replacements, and inspections across
              a range of building types, from single-family homes to commercial and industrial facilities.
            </p>

            <div className="paraBreak" aria-hidden="true">—</div>

            <p>
              This site is organized to help visitors understand their roofing options, review how similar projects have
              been handled, and determine whether Crowned Roofing is the right fit for their needs. Residential and
              commercial services are outlined separately to reflect the different considerations involved, while
              inspection services and project examples provide additional context and transparency.
            </p>

            <div className="paraBreak" aria-hidden="true">—</div>

            <p>
              Rather than relying on sales-driven recommendations, our approach is built around evaluating existing
              conditions, defining scopes based on real findings, and completing work according to manufacturer
              requirements and industry standards. The goal is to support informed decisions—whether that means a
              targeted repair, a phased plan, or a full replacement.
            </p>

            <button className="btn primary" onClick={() => navigate("/contact")}>
              CONTACT US
            </button>
          </div>
        </div>
      </section>

      {/* CASE STUDIES */}
      <section className="lp cases snap-section" ref={sCasesRef}>
        <div className="container">
          <h2 className="sectionTitle centered">
            <span className="accentBar" aria-hidden="true" />
            Case Study Highlights
          </h2>

          <div className={`caseGrid ${casesIn ? "in" : ""}`}>
            {[
              {
                title: "Commercial Facility — Lifecycle Repair Planning",
                loc: "Burlington, ON",
                img: `${pubUrl}/images/landing/case-1.png`,
                situation:
                  "Widespread surface wear with limited moisture intrusion; ownership needed to evaluate repair viability versus replacement planning.",
                approach:
                  "Documented conditions and developed a repair scope focused on extending service life while supporting future capital planning.",
                outcome:
                  "Repairs stabilized the roof system and supported a measured replacement timeline.",
              },
              {
                title: "Single-Family Home — Full Roof Replacement",
                loc: "Markham, ON",
                img: `${pubUrl}/images/landing/case-2.png`,
                situation:
                  "Roof reached end of service life with consistent wear across multiple slopes and transitions.",
                approach:
                  "Defined a full replacement scope addressing underlayment, flashing, ventilation, and installation sequencing.",
                outcome:
                  "New system installed cleanly and documented for long-term performance and clarity moving forward.",
              },
              {
                title: "Light Industrial Building — Targeted Leak Resolution",
                loc: "Ajax, ON",
                img: `${pubUrl}/images/landing/case-3.png`,
                situation:
                  "Intermittent leaks reported near multiple penetrations with no clear pattern of failure.",
                approach:
                  "Traced moisture paths, identified contributing details, and isolated repairs to affected areas.",
                outcome:
                  "Leaks resolved with minimal disruption and without expanding scope beyond what was necessary.",
              },
              {
                title: "Single-Family Home — Storm Damage Assessment",
                loc: "Toronto, ON",
                img: `${pubUrl}/images/landing/case-4.png`,
                situation:
                  "Post-storm concerns about damage; homeowner needed clarity on repair versus replacement.",
                approach:
                  "Evaluated condition, documented findings, and reviewed options based on observed damage.",
                outcome:
                  "Proceeding plan addressed affected areas without unnecessary replacement.",
              },
            ].map((c, i) => (
              <article className="caseCard" style={{ transitionDelay: `${i * 90}ms` }} key={c.title}>
                <h3 className="caseTitle">{c.title}</h3>
                <div className="caseLoc">{c.loc}</div>
                <div className="caseImgWrap">
                  <img src={c.img} alt="" aria-hidden="true" />
                </div>

                <div className="caseRow">
                  <div className="kicker">Situation</div>
                  <p>{c.situation}</p>
                </div>
                <div className="caseRow">
                  <div className="kicker">Approach</div>
                  <p>{c.approach}</p>
                </div>
                <div className="caseRow">
                  <div className="kicker">Outcome</div>
                  <p>{c.outcome}</p>
                </div>
              </article>
            ))}
          </div>

          <button className="linkArrow" onClick={() => navigate("/portfolio")}>
            View All Case Studies <span aria-hidden="true">›</span>
          </button>
        </div>
      </section>

      {/* CLIENT FEEDBACK */}
      <section className="lp feedback snap-section" ref={sFeedbackRef}>
        <div className={`container feedbackInner ${feedbackIn ? "in" : ""}`}>
          <h2 className="sectionTitle centered">
            <span className="accentBar" aria-hidden="true" />
            Client Feedback
          </h2>
          <div className="subhead">A selection of feedback from commercial and residential clients</div>

          <div className="quoteList">
            <div className="quoteBlock">
              <div className="quoteTitle">“Clear explanations and no pressure”</div>
              <div className="quoteText">
                “Crowned Roofing walked us through the condition of our roof and explained the options without pushing a
                replacement. The scope made sense, the work was clean, and everything matched what was discussed.”
              </div>
              <div className="quoteBy">— Mark R., Toronto, ON</div>
            </div>

            <div className="quoteBlock">
              <div className="quoteTitle">“Straightforward from start to finish”</div>
              <div className="quoteText">
                “Communication was consistent and easy to follow. The crew showed up when scheduled, worked efficiently,
                and left the site clean. There were no surprises along the way.”
              </div>
              <div className="quoteBy">— Lisa M., Oshawa, ON</div>
            </div>

            <div className="quoteBlock">
              <div className="quoteTitle">“Focused on what actually needed to be done”</div>
              <div className="quoteText">
                “We appreciated that they identified the source of the problem instead of expanding the scope. The
                repair addressed the issue without unnecessary work or added costs.”
              </div>
              <div className="quoteBy">— Daniel S., Richmond Hill, ON</div>
            </div>

            <div className="quoteBlock">
              <div className="quoteTitle">“Professional and well-documented”</div>
              <div className="quoteText">
                “The documentation and explanations made decision-making straightforward. The repair was handled
                properly, and the process was clear from evaluation through close-out.”
              </div>
              <div className="quoteBy">— Facility Manager, Hamilton, ON</div>
            </div>
          </div>
        </div>
      </section>

      {/* CHOOSE NEXT STEP */}
      <section className="lp choice snap-section" ref={sChoiceRef}>
        <div className={`choiceBg ${choiceIn ? "in" : ""}`} aria-hidden="true">
          <div className={`bgLayer ${choiceBg === "res" ? "show" : ""}`} style={{ backgroundImage: `url(${pubUrl}/images/landing/btn-res.png)` }} />
          <div className={`bgLayer ${choiceBg === "com" ? "show" : ""}`} style={{ backgroundImage: `url(${pubUrl}/images/landing/btn-com.png)` }} />
        </div>

        <div className="container choiceInner">
          <h2 className="choiceTitle">Choose Your Next Step</h2>
          <div className="choiceSub">Select the service that best matches your project.</div>

          <div className="choiceButtons">
            <button
              className="choiceBtn"
              onMouseEnter={() => setChoiceBg("res")}
              onMouseLeave={() => setChoiceBg("none")}
              onClick={goResidential}
            >
              <div className="choiceBtnTitle">Residential</div>
              <div className="choiceBtnSub">Homes and townhomes</div>
            </button>

            <button
              className="choiceBtn"
              onMouseEnter={() => setChoiceBg("com")}
              onMouseLeave={() => setChoiceBg("none")}
              onClick={goCommercial}
            >
              <div className="choiceBtnTitle">Commercial</div>
              <div className="choiceBtnSub">Commercial and industrial buildings</div>
            </button>
          </div>
        </div>
      </section>
      <section className="lpFaq">
        <div className="container">
          <h2 className="sectionTitle centered">
            <span className="accentBar" aria-hidden="true" />
            Roofing FAQ
          </h2>
          <div className="subhead">
            In-depth answers for the questions property owners usually need before they commit to a repair or replacement.
          </div>

          <div className="faqGrid">
            {featuredFaqs.map((faq) => (
              <article key={faq.slug} className="faqCard">
                <div className="faqCardCategory">{faq.category}</div>
                <h3>{faq.question}</h3>
                <p>{faq.description}</p>
                <button className="faqLinkBtn" onClick={() => navigate(`/faq/${faq.slug}`)}>
                  Read Full Answer
                </button>
              </article>
            ))}
          </div>

          <button className="linkArrow" onClick={() => navigate("/faq")}>
            View All Roofing FAQ <span aria-hidden="true">â€º</span>
          </button>
        </div>
      </section>
      <footer className='lpFooter'>
        <Footer/>
      </footer>
    </main>
  );
}
